import type { Genome } from '@/domain/genetics/types';

export type TemperamentId = 'calm' | 'needy' | 'playful' | 'glutton' | 'stubborn' | 'fragile' | 'tidy';

export interface TemperamentProfile {
  primary: TemperamentId;
  secondary: TemperamentId | null;
  weights: Record<TemperamentId, number>;
}

export const ALL_TEMPERAMENTS: ReadonlyArray<TemperamentId> = [
  'calm',
  'needy',
  'playful',
  'glutton',
  'stubborn',
  'fragile',
  'tidy',
];

export function deriveTemperament(genome: Genome): TemperamentProfile {
  const weights = createWeights();

  for (const type of genome.primaryTypes) {
    if (type === 'Earth') weights.calm += 5;
    if (type === 'Life') {
      weights.needy += 3;
      weights.tidy += 2;
    }
    if (type === 'Water') {
      weights.tidy += 5;
      weights.needy += 1;
    }
    if (type === 'Fire') {
      weights.playful += 4;
      weights.glutton += 1;
    }
    if (type === 'Air') weights.playful += 4;
    if (type === 'Shadow') {
      weights.stubborn += 4;
      weights.fragile += 3;
    }
  }

  if (genome.body === 'fish') weights.tidy += 3;
  if (genome.body === 'ghost') {
    weights.stubborn += 2;
    weights.calm += 1;
  }
  if (genome.body === 'blob') weights.glutton += 2;
  if (genome.body === 'tall') weights.calm += 1;

  if (genome.ears === 'antenna') weights.fragile += 1;
  if (genome.ears === 'pointy') weights.playful += 1;
  if (genome.tail === 'fin') weights.tidy += 1;
  if (genome.tail === 'curl') weights.playful += 1;
  if (genome.markings === 'cheeks') weights.playful += 1;
  if (genome.markings === 'mask') weights.stubborn += 1;

  if (genome.bodyPredisposition <= 35) weights.fragile += 1;
  if (genome.bodyPredisposition >= 70) weights.calm += 1;

  const ranked = [...ALL_TEMPERAMENTS].sort(
    (a, b) => weights[b] - weights[a] || ALL_TEMPERAMENTS.indexOf(a) - ALL_TEMPERAMENTS.indexOf(b),
  );
  const primary = ranked[0]!;
  const secondary = weights[ranked[1]!] > 1 ? ranked[1]! : null;

  return { primary, secondary, weights };
}

export function hasTemperament(profile: TemperamentProfile, id: TemperamentId): boolean {
  return profile.primary === id || profile.secondary === id;
}

function createWeights(): Record<TemperamentId, number> {
  return {
    calm: 1,
    needy: 1,
    playful: 1,
    glutton: 1,
    stubborn: 1,
    fragile: 1,
    tidy: 1,
  };
}
